// src/pages/Cloud/ComplianceOverview.js
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Input, Progress, Alert } from 'reactstrap';
import { Card, Container, Form, Spinner, Image } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import ComplianceCircle from './charts/ComplianceCircle';
import ComplianceDetailTable from './charts/ComplianceDetailTable';
import FSCOverviewPanel from './components/fsc/FSCOverviewPanel';
import CloudScopeSelector from './components/CloudScopeSelector';
import { getCloudScopeConfig, getSecondaryScopeConfig } from './cloudScopeConfig';

import {
  fetchAzureRiskAssessment,
  fetchComplianceSummary,
  fetchComplianceDetail,
  fetchAssessmentResources,
  getAssessmentResourceKey,
  fetchCloudScopes,
  fetchSecondaryScopes,
  setSelectedStandard,
  setSelectedCloudScope,
  setSelectedSecondaryScope,
  resetCloudScopes,
  clearComplianceDetail,
} from '../../store/slices/complianceSlice';
import { normalizeProjectUuid } from '../../api/projectContext';

import { cloudNativeEnabledSelector } from '../../store/selectors/licenseSelectors';
import { AWS_CIS_FOUNDATIONS_V3, AWS_FSBP_V1 } from '../../constants/awsComplianceStandards';

const GCP_CIS_SUMMARY_KEYS = [
  'CIS Google Cloud Platform Foundation 2.0',
  'CIS Google Cloud Platform Foundation v2.0.0',
];

/** 嚴重度色票；總嚴重風險數用比總高風險數更深的紅，維持同一個紅色階 */
const RISK_COLORS = {
  critical: '#A32D2D',
  high: '#C5283D',
  medium: '#CB761B',
  low: '#138854',
};

function getProjectUuidFromLocalStorage() {
  const raw = localStorage.getItem('project_uuid');
  return normalizeProjectUuid(raw);
}

export default function ComplianceOverview({ platform = 'azure' }) {
  const dispatch = useDispatch();

  const projectUuid = useMemo(() => getProjectUuidFromLocalStorage(), []);

  const {
    complianceSummary,
    complianceDetail,
    assessmentResources,
    statistics,
    loading,
    summaryLoading,
    detailLoading,
    resourcesLoading,
    selectedStandard,
    cloudScopes,
    selectedCloudScope,
    scopesLoading,
    scopesLoaded,
    scopesError,
    secondaryScopes,
    selectedSecondaryScope,
    secondaryScopesLoading,
    secondaryScopesError,
    realtimeWarnings,
  } = useSelector((state) => state.compliance);

  const scopeConfig = useMemo(() => getCloudScopeConfig(platform), [platform]);
  const secondaryScopeConfig = useMemo(() => getSecondaryScopeConfig(platform), [platform]);

  const authorityCodes = useSelector(state => state.businessMembers.authorityCodes);
  const cloudNativeEnabled = useSelector(cloudNativeEnabledSelector);
  const source = "cloudnative";
  const cloud = platform;
  const isGcp = platform === 'gcp';
  const isAws = platform === 'aws';
  const platformTitle = isGcp
    ? 'Google Cloud Platform'
    : isAws
      ? 'Amazon Web Services'
      : 'Microsoft Azure';

  // 標準選項列表
  const standards = useMemo(() => {
    if (isGcp) {
      return [
        { id: 'total', label: 'Google Cloud Platform合規總表', apiKey: 'total' },
        {
          id: 'cis',
          label: 'CIS Google Cloud Platform Foundation 2.0',
          apiKey: GCP_CIS_SUMMARY_KEYS[0],
        },
        { id: 'iso_27001', label: 'ISO 27001 2022', apiKey: 'ISO 27001 2022' },
        { id: 'fsc', label: '金管會雲端資安基準', apiKey: '金管會雲端資安基準', isManual: true },
      ];
    }
    if (isAws) {
      return [
        { id: 'total', label: 'Amazon Web Services合規總表', apiKey: 'total' },
        {
          id: 'cis',
          label: AWS_CIS_FOUNDATIONS_V3,
          apiKey: AWS_CIS_FOUNDATIONS_V3,
        },
        {
          id: 'fsbp',
          label: AWS_FSBP_V1,
          apiKey: AWS_FSBP_V1,
        },
        { id: 'fsc', label: '金管會雲端資安基準', apiKey: '金管會雲端資安基準', isManual: true },
      ];
    }
    return [
      {
        id: 'total',
        label: 'Microsoft Azure合規總表',
        apiKey: 'total',
      },
      { id: 'iso_27001', label: 'Azure ISO IEC 27001:2022', apiKey: 'Azure ISO IEC 27001:2022' },
      { id: 'cis', label: 'CIS Azure Foundations v2.0.0', apiKey: 'CIS Azure Foundations v2.0.0' },
      { id: 'mcsb', label: 'Microsoft cloud security benchmark', apiKey: 'Microsoft cloud security benchmark' },
      { id: 'fsc', label: '金管會雲端資安基準', apiKey: '金管會雲端資安基準', isManual: true },
    ];
  }, [isGcp, isAws]);

  const standardsMapping = useMemo(() => {
    if (isGcp) {
      return {
        total: { riskApi: 'all', detailApi: null },
        iso_27001: { riskApi: 'iso', detailApi: 'iso_27001' },
        cis: { riskApi: 'cis', detailApi: 'cis' },
      };
    }
    if (isAws) {
      return {
        total: { riskApi: 'all', detailApi: null },
        cis: { riskApi: 'cis', detailApi: 'cis' },
        fsbp: { riskApi: 'fsbp', detailApi: 'fsbp' },
      };
    }
    return {
      total: { riskApi: 'all', detailApi: null },
      iso_27001: { riskApi: 'iso', detailApi: 'iso_27001' },
      cis: { riskApi: 'cis', detailApi: 'cis' },
      mcsb: { riskApi: 'mcsb', detailApi: 'mcsb' },
    };
  }, [isGcp, isAws]);

  // 確認授權與角色權限
  const availableStandardIds = useMemo(() => {
    if (!cloudNativeEnabled || !authorityCodes || authorityCodes.length === 0) return [];
  
    const prefix = `${source}_${cloud}_`; // cloudnative_azure_

    return authorityCodes
      .filter(code => code.startsWith(prefix))
      .map(code => {
        const lastPart = code.split("_").pop();
        if (lastPart === "summary") return "total";
        if (lastPart === "iso27001") return "iso_27001";
        return lastPart;
      })
      .filter(id => standards.some(std => std.id === id));
  }, [authorityCodes, standards, cloudNativeEnabled, cloud]);

  // total 卡片顯示配置
  const complianceStandards = useMemo(() => {
    if (isGcp) {
      return [
        {
          id: 'cis',
          title: 'CIS Google Cloud Platform Foundation 2.0',
          apiKey: GCP_CIS_SUMMARY_KEYS[0],
        },
        { id: 'iso_27001', title: 'ISO 27001 2022', apiKey: 'ISO 27001 2022' },
      ];
    }
    if (isAws) {
      return [
        {
          id: 'cis',
          title: AWS_CIS_FOUNDATIONS_V3,
          apiKey: AWS_CIS_FOUNDATIONS_V3,
        },
        {
          id: 'fsbp',
          title: AWS_FSBP_V1,
          apiKey: AWS_FSBP_V1,
        },
      ];
    }
    return [
        { id: 'iso_27001', title: 'Azure ISO IEC 27001:2022', apiKey: 'Azure ISO IEC 27001:2022' },
        {
          id: 'cis',
          title: 'CIS Microsoft Azure Foundations Benchmark v2.0.0',
          apiKey: 'CIS Azure Foundations v2.0.0',
        },
        {
          id: 'mcsb',
          title: 'Microsoft cloud security benchmark (MCSB)',
          apiKey: 'Microsoft cloud security benchmark',
        },
      ];
  }, [isGcp, isAws]);

  useEffect(() => {
    if (!standards.some((s) => s.id === selectedStandard)) {
      dispatch(setSelectedStandard('total'));
    }
  }, [dispatch, selectedStandard, standards]);

  const emptySummary = useMemo(
    () => ({ passed_count: 0, total_count: 0, failed_count: 0, compliance_percentage: 0 }),
    []
  );

  const getSummaryByApiKey = useCallback(
    (apiKey) => {
      if (!apiKey) return emptySummary;
      if (Array.isArray(apiKey)) {
        const found = apiKey.map((key) => complianceSummary[key]).find(Boolean);
        return found || emptySummary;
      }
      return complianceSummary[apiKey] || emptySummary;
    },
    [complianceSummary, emptySummary]
  );

  // 取得當前選中標準的合規數據（摘要）- 只給非 FSC 使用
  const currentStandardCompliance = useMemo(() => {
    if (isGcp && selectedStandard === 'total') {
      const totalCount = statistics.total || 0;
      const passedCount = statistics.passed || 0;
      const failedCount = statistics.failed || 0;
      const compliancePercentage =
        totalCount > 0 ? Math.round((passedCount / totalCount) * 100 * 100) / 100 : 0;

      return {
        passed_count: passedCount,
        total_count: totalCount,
        failed_count: failedCount,
        compliance_percentage: compliancePercentage,
      };
    }

    if (isAws && selectedStandard === 'total') {
      return getSummaryByApiKey('total');
    }

    const standard = standards.find((s) => s.id === selectedStandard);
    if (!standard || !standard.apiKey) return emptySummary;
    return getSummaryByApiKey(standard.apiKey);
  }, [selectedStandard, standards, isGcp, isAws, statistics, emptySummary, getSummaryByApiKey]);

  const fscTotalSummary = useMemo(() => {
    return (
      complianceSummary['金管會雲端資安基準'] || {
        passed_count: 0,
        total_count: 0,
        failed_count: 0,
        compliance_percentage: 0,
      }
    );
  }, [complianceSummary]);

  const fscCisSummary = useMemo(() => {
    if (isGcp) {
      const found = GCP_CIS_SUMMARY_KEYS.map((key) => complianceSummary[key]).find(Boolean);
      return found || emptySummary;
    }
    if (isAws) {
      return complianceSummary[AWS_CIS_FOUNDATIONS_V3] || emptySummary;
    }
    return complianceSummary['CIS Azure Foundations v2.0.0'] || emptySummary;
  }, [complianceSummary, isGcp, isAws, emptySummary]);

  // 先取得查詢範圍清單，確保後續請求一開始就帶正確的 cloud_scope
  useEffect(() => {
    dispatch(resetCloudScopes());
    dispatch(fetchCloudScopes({ platform }));
  }, [dispatch, platform]);

  // Region 清單依帳戶而定，帳戶確定後才查得到
  useEffect(() => {
    if (!secondaryScopeConfig || !scopesLoaded) return;
    if (!selectedCloudScope) return;
    dispatch(fetchSecondaryScopes({ platform }));
  }, [dispatch, platform, scopesLoaded, selectedCloudScope, secondaryScopeConfig]);

  const scopeSignature = `${platform}|${selectedCloudScope}|${selectedSecondaryScope}`;
  // 元件重掛時 store 可能仍留著上一頁的 scopesLoaded=true；先視為已處理，
  // 等本次 reset/fetch scopes 進入 loading 後再把 ref 清掉。
  const previousSummaryScopeRef = useRef(scopeSignature);
  const previousRiskQueryRef = useRef(`${scopeSignature}|${selectedStandard}`);

  // 初次載入與 scope 切換共用同一條路徑，避免 AWS 預設帳戶落定時重複查詢。
  useEffect(() => {
    if (!scopesLoaded) {
      previousSummaryScopeRef.current = null;
      return;
    }
    if (previousSummaryScopeRef.current === scopeSignature) return;
    previousSummaryScopeRef.current = scopeSignature;

    dispatch(fetchComplianceSummary({ platform }));
  }, [dispatch, platform, scopesLoaded, scopeSignature]);

  // 標準或 scope 改變時只送出一組 risk/detail request。
  useEffect(() => {
    if (!scopesLoaded) {
      previousRiskQueryRef.current = null;
      return;
    }
    if (!standards.some((standard) => standard.id === selectedStandard)) return;

    const querySignature = `${scopeSignature}|${selectedStandard}`;
    if (previousRiskQueryRef.current === querySignature) return;
    previousRiskQueryRef.current = querySignature;
    if (selectedStandard === 'fsc') return;

    const mapping = standardsMapping[selectedStandard];
    dispatch(
      fetchAzureRiskAssessment({
        risk_type: 'summary',
        standard: mapping?.riskApi || 'all',
        platform,
      })
    );

    if (selectedStandard !== 'total' && mapping?.detailApi) {
      dispatch(fetchComplianceDetail({ standardId: mapping.detailApi, platform }));
    } else {
      dispatch(clearComplianceDetail());
    }
  }, [
    dispatch,
    platform,
    scopesLoaded,
    scopeSignature,
    selectedStandard,
    standards,
    standardsMapping,
  ]);

  const handleStandardChange = (e) => {
    const standardId = e.target.value;

    dispatch(setSelectedStandard(standardId));
    dispatch(clearComplianceDetail());
  };

  // 後端有回 CriticalRiskCount 的雲才拆四級（AWS／GCP）；
  // Azure 沒有這個等級，維持原本三張卡 + 總風險數的排法
  const hasCriticalSeverity = statistics.criticalRisk != null;

  const renderRiskCard = ({ value, color, icon, alt, label }, className) => (
    <Card style={{ minHeight: '150px' }} className={className}>
      <Row className="mx-2">
        <Col className="p-4">
          <h2 style={{ fontWeight: 'bold', color }}>
            {loading ? <Spinner animation="border" size="sm" /> : value}
          </h2>
        </Col>
      </Row>
      <Row className="mx-1">
        <Col>
          <Image alt={alt} src={`/assets/images/${icon}.svg`} style={{ display: 'inline' }} />
          <span style={{ fontWeight: 'bold', color: '#5F5F5F' }}>{label}</span>
        </Col>
      </Row>
    </Card>
  );

  const severityCards = useMemo(() => {
    const high = {
      value: statistics.highRisk,
      color: RISK_COLORS.high,
      icon: 'ic_trending_up_FILL0_wght400_GRAD0_opsz24',
      alt: 'high-risks',
      label: '總高風險數',
    };
    const medium = {
      value: statistics.mediumRisk,
      color: RISK_COLORS.medium,
      icon: 'ic_read_more_FILL0_wght400_GRAD0_opsz24',
      alt: 'mid-risks',
      label: '總中風險數',
    };
    const low = {
      value: statistics.lowRisk,
      color: RISK_COLORS.low,
      icon: 'ic_trending_down_FILL0_wght400_GRAD0_opsz24',
      alt: 'low-risks',
      label: '總低風險數',
    };

    if (!hasCriticalSeverity) return [high, medium, low];

    return [
      {
        value: statistics.criticalRisk,
        color: RISK_COLORS.critical,
        icon: 'ic_warning_FILL0_wght400_GRAD0_opsz24',
        alt: 'critical-risks',
        label: '總嚴重風險數',
      },
      high,
      medium,
      low,
    ];
  }, [hasCriticalSeverity, statistics]);

  const handleCloudScopeChange = (scopeId) => {
    dispatch(setSelectedCloudScope(scopeId));
  };

  const handleSecondaryScopeChange = (scopeId) => {
    dispatch(setSelectedSecondaryScope(scopeId));
  };

  const handleLoadResources = (assessmentId, assessmentName, scanMonth = null) => {
    if (!assessmentResources[getAssessmentResourceKey(assessmentId, scanMonth)]) {
      dispatch(fetchAssessmentResources({ assessmentId, assessmentName, platform, scanMonth }));
    }
  };

  // 載入狀態（維持原本）
  if ((loading || summaryLoading || !scopesLoaded) && !statistics.total) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">載入中</span>
          </Spinner>
          <p>正在載入合規評估資料</p>
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Container className="mt-2">
        <Row className="align-items-center mb-4">
          <Col className="mt-3">
            <h4 style={{ fontWeight: 'bold' }}>{platformTitle}</h4>
          </Col>

          {scopeConfig.enabled && (
            <Col xs={secondaryScopeConfig ? 2 : 3}>
              <Form>
                <CloudScopeSelector
                  label={scopeConfig.label}
                  value={selectedCloudScope}
                  options={cloudScopes}
                  loading={scopesLoading}
                  includeAll={scopeConfig.includeAll !== false}
                  onChange={handleCloudScopeChange}
                />
              </Form>
            </Col>
          )}

          {secondaryScopeConfig && (
            <Col xs={2}>
              <Form>
                <CloudScopeSelector
                  label={secondaryScopeConfig.label}
                  value={selectedSecondaryScope}
                  options={secondaryScopes}
                  loading={secondaryScopesLoading}
                  includeAll={secondaryScopeConfig.includeAll !== false}
                  onChange={handleSecondaryScopeChange}
                />
              </Form>
            </Col>
          )}

          <Col className="float-end" xs={4}>
            <Form>
              <Form.Group>
                <Form.Label>
                  <span style={{ fontWeight: 'bold' }}>項目名稱</span>
                </Form.Label>

                <Input type="select" bsSize="sm" value={selectedStandard} onChange={handleStandardChange}>
                  {standards
                    .filter((std) => availableStandardIds.includes(std.id))
                    .map((std) => (
                      <option key={std.id} value={std.id}>
                        {std.label}
                      </option>
                    ))}
                </Input>
              </Form.Group>
            </Form>
          </Col>
        </Row>

        {scopeConfig.enabled && scopesError && (
          <Alert color="warning" className="py-2">
            {`${scopeConfig.label}清單載入失敗，目前以預設範圍顯示：${scopesError}`}
          </Alert>
        )}

        {secondaryScopeConfig && secondaryScopesError && (
          <Alert color="warning" className="py-2">
            {`${secondaryScopeConfig.label}清單載入失敗，目前以全部範圍顯示：${secondaryScopesError}`}
          </Alert>
        )}

        {/* 權限不足或某些區域／標準查詢失敗時，數字會不完整，不能讓它悄悄過去 */}
        {realtimeWarnings && realtimeWarnings.length > 0 && (
          <Alert color="warning" className="py-2">
            <div style={{ fontWeight: 'bold' }}>部分查詢未完成，以下數字可能不完整</div>
            <ul className="mb-0 ps-3">
              {realtimeWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* FSC：完全交給獨立元件處理，避免影響原本總表/風險 UI */}
        {selectedStandard === 'fsc' ? (
          <FSCOverviewPanel
            projectUuid={projectUuid}
            platform={platform}
            assessmentResources={assessmentResources}
            resourcesLoading={resourcesLoading}
            onLoadResources={handleLoadResources}
            cisSummary={fscCisSummary}
          />
        ) : (
          <>
            <Row>
              <Col>
                <h5 style={{ fontWeight: 'bold' }}>總風險評估</h5>
              </Col>
            </Row>

            <Row className="mt-3 g-3">
              {hasCriticalSeverity ? (
                /* 有 Critical 的雲（AWS／GCP）：2×2 嚴重度格 + 整寬總風險數橫條 */
                <Col md={8}>
                  <Row className="g-3">
                    {severityCards.map((card) => (
                      <Col md={6} key={card.label}>
                        {renderRiskCard(card)}
                      </Col>
                    ))}
                  </Row>

                  <Card style={{ minHeight: '80px' }} className="mt-3">
                    {/* 圖示、標籤、數字視為一組置中內容，數字不貼齊卡片右緣 */}
                    <Row className="mx-1 h-100 align-items-center justify-content-center py-3 g-3">
                      <Col xs="auto">
                        <Image
                          alt="total-risks"
                          src="/assets/images/ic_bar_chart_24dp_323232_FILL1_wght400_GRAD0_opsz24.svg"
                          style={{ display: 'inline' }}
                        />
                        <span style={{ fontWeight: 'bold', color: '#5F5F5F' }}>總風險數</span>
                      </Col>
                      <Col xs="auto">
                        <h2 style={{ fontWeight: 'bold', marginBottom: 0 }}>
                          {loading ? <Spinner animation="border" size="sm" /> : statistics.totalRisk}
                        </h2>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ) : (
                /* 沒有 Critical 的雲（Azure）：維持原本兩欄各兩張卡 */
                <>
                  <Col md={4}>
                    {renderRiskCard(severityCards[0])}
                    {renderRiskCard(severityCards[2], 'mt-3')}
                  </Col>

                  <Col md={4}>
                    {renderRiskCard(severityCards[1])}
                    {renderRiskCard(
                      {
                        value: statistics.totalRisk,
                        icon: 'ic_bar_chart_24dp_323232_FILL1_wght400_GRAD0_opsz24',
                        alt: 'total-risks',
                        label: '總風險數',
                      },
                      'mt-3'
                    )}
                  </Col>
                </>
              )}

              <Col md={4}>
                <Card style={{ height: '100%' }} className="d-flex flex-column">
                  <Card.Body>
                    <h5 className="d-flex justify-content-center" style={{ display: 'inline', fontWeight: 'bold' }}>
                      總合規通過數
                    </h5>

                    <div className="d-flex justify-content-center flex-grow-1 align-items-center">
                      {summaryLoading ? (
                        <Spinner animation="border" />
                      ) : (
                        <ComplianceCircle
                          passed={currentStandardCompliance.passed_count}
                          total={currentStandardCompliance.total_count}
                          size={180}
                          compliancePercentage={currentStandardCompliance.compliance_percentage}
                        />
                      )}
                    </div>

                    <Row className="mt-auto text-center">
                      <Col>
                        <div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                            {summaryLoading ? '-' : currentStandardCompliance.passed_count}
                          </div>
                          <small className="text-muted">總合規通過數</small>
                        </div>
                      </Col>
                      <Col>
                        <div>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6c757d' }}>
                            {summaryLoading ? '-' : currentStandardCompliance.total_count}
                          </div>
                          <small className="text-muted">總合規數量</small>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {selectedStandard && selectedStandard === 'total' && (
              <Row className="mt-4">
                {complianceStandards.map((standard) => {
                  const standardData = getSummaryByApiKey(standard.apiKey);

                  return (
                    <Col key={standard.id} md={6} className="mb-4">
                      <Card style={{ minHeight: '180px' }} className="h-100">
                        <Card.Body className="d-flex flex-column">
                          <Row className="mb-3">
                            <Col>
                              <h6 style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>{standard.title}</h6>
                            </Col>
                          </Row>

                          <Row className="mb-2 align-items-center">
                            <Col xs={3}>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#323232' }}>
                                {summaryLoading ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  `${Math.round(standardData.compliance_percentage)}%`
                                )}
                              </div>
                            </Col>

                            <Col xs={9}>
                              <Progress
                                value={standardData.compliance_percentage}
                                style={{ height: '20px' }}
                                color={'#72A3FF'}
                                className="mb-2"
                              />
                              <div className="d-flex justify-content-between">
                                <small style={{ color: '#6c757d' }}>總合規通過數</small>
                                <small style={{ color: '#6c757d' }}>總合規數</small>
                              </div>
                              <div className="d-flex justify-content-between" style={{ fontWeight: 'bold' }}>
                                <span>{summaryLoading ? '-' : standardData.passed_count}</span>
                                <span>{summaryLoading ? '-' : standardData.total_count}</span>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}

                <Col md={6} className="mb-4">
                  <Card style={{ minHeight: '180px' }} className="h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="mb-3">
                        <Col>
                          <h6 style={{ fontWeight: 'bold', fontSize: '0.95rem' }}>金管會</h6>
                        </Col>
                      </Row>

                      <Row className="mb-2 align-items-center">
                        <Col xs={3}>
                          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#323232' }}>
                            {summaryLoading ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              `${Math.round(fscTotalSummary.compliance_percentage)}%`
                            )}
                          </div>
                        </Col>
                        <Col xs={9}>
                          <Progress
                            value={fscTotalSummary.compliance_percentage}
                            style={{ height: '20px' }}
                            className="mb-2"
                          />
                          <div className="d-flex justify-content-between">
                            <small style={{ color: '#6c757d' }}>總合規通過數</small>
                            <small style={{ color: '#6c757d' }}>總合規數</small>
                          </div>
                          <div className="d-flex justify-content-between" style={{ fontWeight: 'bold' }}>
                            <span>{summaryLoading ? '-' : fscTotalSummary.passed_count}</span>
                            <span>{summaryLoading ? '-' : fscTotalSummary.total_count}</span>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* 非 total：維持原本單一標準明細（不含 FSC） */}
            {selectedStandard && selectedStandard !== 'total' && (
              <Row className="mt-4">
                <Col>
                  <Card>
                    <Card.Header>
                      <h5 style={{ fontWeight: 'bold' }}>控制項詳細列表</h5>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <ComplianceDetailTable
                        controls={complianceDetail.controls}
                        assessmentResources={assessmentResources}
                        loading={detailLoading}
                        resourcesLoading={resourcesLoading}
                        onLoadResources={handleLoadResources}
                        months={complianceDetail.months}
                        currentMonthKey={complianceDetail.currentMonthKey}
                        compliancePlatform={platform}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>
    </React.Fragment>
  );
}
